import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import Login from './pages/Login';
import Register from './pages/Register';
import Admins from './pages/Admins';
import Workflows from './pages/Workflows';
import Webhooks from './pages/Webhooks';
import AiConnectors from './pages/AiConnectors';
import Settings from './pages/Settings';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev: boolean) => !prev);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);

    const handleUpdate = (event: CustomEvent<{ onUpdate: () => void }>) => {
      const { onUpdate } = event.detail;
      toast.info('✨ A new version is available! Updating...', {
        autoClose: 3000,
        onClose: () => {
          setTimeout(() => onUpdate(), 500);
        },
      });
    };

    window.addEventListener('app-update-available', handleUpdate as EventListener);
    return () => {
      window.removeEventListener('app-update-available', handleUpdate as EventListener);
    };
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        <ToastContainer position="top-center" autoClose={3000} theme={darkMode ? 'dark' : 'light'} />
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Register setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/workflows" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admins"
            element={
              isAuthenticated ? (
                <Admins setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-connectors"
            element={
              isAuthenticated ? (
                <AiConnectors setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/webhooks"
            element={
              isAuthenticated ? (
                <Webhooks setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/workflows"
            element={
              isAuthenticated ? (
                <Workflows setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/workflows/runs/:runId"
            element={
              isAuthenticated ? (
                <Workflows setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/workflows/:id"
            element={
              isAuthenticated ? (
                <Workflows setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Settings setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
